/**
 * GAP2-04 (#212), recommendation 4: make the blog's contribution measurable.
 *
 * The "Recommended by Dr Duncan" block gives 220 articles their first path to a product. It
 * shipped with no instrumentation, which means that in a fortnight nobody could say whether it
 * earned anything — and an unmeasured block is the first thing dropped in the next redesign.
 *
 * Two GA4 events, and only two: `view_item_list` when the block is actually seen, and
 * `select_item` when a card is clicked. Both follow GA4's own shape — `ecommerce.items`, not a
 * flat bag of custom keys (see #366, where the theme's existing custom events do not).
 *
 * WHY IntersectionObserver AND NOT ON LOAD. The block sits below the article body. Firing
 * `view_item_list` at page load would report an impression to every reader who bounced off the
 * first paragraph, and the number this exists to produce — did the article send anybody to a
 * product — would be wrong in the flattering direction from the first day.
 *
 * WHAT IT DOES NOT DO. It sends nothing itself: one `dataLayer` push each, and GTM decides what
 * leaves the browser. That keeps it behind whatever consent gate is eventually configured
 * (GAP3-02, #218) rather than becoming a second thing to remember.
 */

/** GA4 wants a plain list of items; the DOM carries it as one escaped attribute. */
export function readItems(section) {
  try {
    const parsed = JSON.parse(section?.dataset?.items || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // A malformed attribute is a reporting bug, never a broken page. The cards still work.
    return [];
  }
}

/** The item a click landed on, matched by the product path in the clicked anchor's href. */
export function itemForHref(items, href) {
  if (!href) return null;
  let path;
  try {
    path = new URL(href, 'https://woopf.com').pathname;
  } catch {
    return null;
  }
  // Locale prefixes (/de/products/...) and query strings both have to fall through to the same
  // item, so the comparison is on the handle rather than on the whole path.
  const handle = /\/products\/([^/?#]+)/.exec(path)?.[1];
  if (!handle) return null;
  return items.find((item) => new RegExp(`/products/${handle}$`).test(item.url || '')) || null;
}

/** One event, shaped the way GA4 reads it. */
export function ecommerceEvent(name, section, items) {
  return {
    event: name,
    ecommerce: {
      currency: section?.dataset?.currency || undefined,
      item_list_id: section?.dataset?.listId || 'article_products',
      item_list_name: section?.dataset?.listName || undefined,
      items,
    },
  };
}

function push(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export function watch(section) {
  const items = readItems(section);
  if (!items.length) return;

  let seen = false;
  const report = () => {
    if (seen) return;
    seen = true;
    push(ecommerceEvent('view_item_list', section, items));
  };

  if (typeof IntersectionObserver === 'function') {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          report();
          observer.disconnect();
        }
      },
      // Half the block, so a list that scrolls past in one flick is not counted as read.
      { threshold: 0.5 },
    );
    observer.observe(section);
  } else {
    // No observer means an old browser, not a reason to lose the click data below.
    report();
  }

  section.addEventListener('click', (event) => {
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor || !section.contains(anchor)) return;
    const item = itemForHref(items, anchor.getAttribute('href'));
    if (!item) return;
    // The list impression has to precede the selection, or GA4 attributes the click to nothing.
    report();
    push(ecommerceEvent('select_item', section, [item]));
  });
}

function init() {
  document.querySelectorAll('[data-article-products]').forEach(watch);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
